import { useForm } from 'react-hook-form'
import { useTranslations } from 'next-intl'
import { IHomePage } from '@/types/strapi/homePage.interface'
import { ReactNode, forwardRef, useState } from 'react'
import type { NextPage, GetStaticPropsContext } from 'next'
import { useMutation } from '@apollo/client'
import { Turnstile } from '@marsidev/react-turnstile'
import {
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  Image,
  Input as CInput,
  InputGroup,
  InputProps,
  Flex,
  Link,
  Icon,
  VStack,
  HStack,
  Select,
  useToast,
} from '@chakra-ui/react'
import { emailToSales } from '@/api/contactSales'
import { TURNSTILE_KEY } from '@/constants'
import { LinkedinIcon } from '@/components/icons/LinkedinIcon'
import SeoHead from '@/components/seo-head'
import { EMAIL_PATTERN, PHONE_NUMBER_PATTERN } from '@/utils/inputPattern'
import { ContainerBase } from '@/components/containers/ContainerBase'

type Props = {
  homePageData: IHomePage
}

type FormValues = {
  company_name: string
  email: string
  phone: string
  message: string
  experienced_miner: boolean
  stages_in: string
  facility_name: string
  facility_location: string
  initial_capacity: string
  site_extendable: boolean
  electricity_agreement: string
}

const Input = forwardRef(({ ...rest }: InputProps, ref) => {
  return (
    <CInput
      ref={ref}
      fontSize={'13px'}
      bgColor={'black.300'}
      border="1px solid"
      borderColor="black.850"
      borderRadius="10px"
      {...rest}
    />
  )
})

interface FieldProps {
  id: string
  label: string
  optional?: boolean
  isInvalid: boolean
  errorMessage?: string
  children: ReactNode
}

const Field = ({
  id,
  label,
  optional,
  isInvalid,
  errorMessage,
  children,
}: FieldProps) => {
  const t = useTranslations()
  return (
    <FormControl id={id} isInvalid={isInvalid}>
      <FormLabel fontSize={'12px'} lineHeight="normal" color="gray.850">
        <HStack>
          <Text as="span">
            {`${label} `}&nbsp;
            {optional && (
              <Text as="span" textTransform="lowercase">
                ({t('contact-sales.form.optional')})
              </Text>
            )}
          </Text>
          <FormErrorMessage
            marginTop="0"
            fontSize={'xs'}
            textTransform="lowercase"
          >
            {errorMessage || t('contact-sales.form.invalid-format')}
          </FormErrorMessage>
        </HStack>
      </FormLabel>
      <InputGroup size="md">{children}</InputGroup>
    </FormControl>
  )
}

const ApplyNowPage: NextPage<Props> = ({ homePageData }) => {
  const t = useTranslations()
  const toast = useToast()
  const [captchaSuccess, setCaptchaSuccess] = useState(false)
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<FormValues>()
  const [sendMail] = useMutation(emailToSales, {
    onCompleted: () => {
      toast({
        title: t('accelerated.form-success'),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
      reset({
        company_name: '',
        email: '',
        phone: '',
        message: '',
        experienced_miner: false,
        stages_in: '',
        facility_name: '',
        facility_location: '',
        initial_capacity: '',
        site_extendable: false,
        electricity_agreement: '',
      })
    },
    onError: () => {
      toast({
        title: t('accelerated.form-error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
    },
  })

  const onSubmit = async (values: FormValues) => {
    sendMail({
      variables: {
        companyName: values.company_name,
        electricityAgreement: values.electricity_agreement,
        email: values.email,
        expandable: values.site_extendable,
        experiencedMiner: values.experienced_miner,
        facilityLocation: values.facility_location,
        facilityName: values.facility_name,
        megawatts: Number(values.initial_capacity),
        phone: values.phone,
        stage: values.stages_in,
      },
    })
  }

  return (
    <>
      <SeoHead title={t('accelerated.seo.title')} />
      <Box bg="black.400" color="white">
        <ContainerBase maxW={{ base: '100%', xl: '1600px' }}>
          <Flex
            alignItems="flex-start"
            padding={{
              base: '32px 16px',
              md: '40px 80px',
              xl: '51px 120px 93px 120px',
            }}
            gridGap={{ base: '40px', md: '60px', xl: '140px', '2xl': '160px' }}
            flexDirection={{ base: 'column', xl: 'row' }}
          >
            <Box flex="1">
              <VStack alignItems="flex-start" spacing="21px">
                <Text
                  as="span"
                  variant="brandGradient"
                  fontWeight="600"
                  fontSize={{ base: '30px', md: '68px' }}
                  lineHeight={{ base: 'normal', md: '74px' }}
                >
                  {t('accelerated.get-started-today')}
                </Text>
                <Text
                  fontSize={{ base: '14px', md: '20px' }}
                  lineHeight={{ base: '24px', md: '27px' }}
                >
                  {t.rich('accelerated.apply-now-content', {
                    br: (children: ReactNode) => <br />,
                  })}
                </Text>
              </VStack>
            </Box>
            <Box flex="1" width={{ base: '100%' }}>
              <VStack alignItems="flex-start" spacing="20px">
                <Field
                  id="company_name"
                  label={t('accelerated.company-name')}
                  isInvalid={!!errors?.company_name}
                  errorMessage={errors?.company_name?.message || ''}
                >
                  <Input
                    type={'text'}
                    placeholder={t('accelerated.company-name')}
                    {...register('company_name', {
                      required: t('forms.errors.is-required'),
                    })}
                    borderColor="gray.850"
                    color="white"
                    _placeholder={{
                      color: 'gray.850',
                    }}
                  />
                </Field>
                <Field
                  id="email"
                  label={t('contact-sales.form.email')}
                  isInvalid={!!errors?.email}
                  errorMessage={errors?.email?.message || ''}
                >
                  <Input
                    type={'text'}
                    placeholder={t('contact-sales.form.email-address')}
                    {...register('email', {
                      required: t('forms.errors.is-required'),
                      validate: {
                        format: (val) => {
                          return EMAIL_PATTERN.test(val)
                        },
                      },
                    })}
                    borderColor="gray.850"
                    color="white"
                    _placeholder={{
                      color: 'gray.850',
                    }}
                  />
                </Field>
                <Field
                  id="phone"
                  label={t('contact-sales.form.phone')}
                  optional={true}
                  isInvalid={!!errors?.phone}
                  errorMessage={errors?.phone?.message || ''}
                >
                  <Input
                    placeholder={'(123) 456-7890'}
                    {...register('phone', {
                      validate: {
                        format: (val) => {
                          return val ? PHONE_NUMBER_PATTERN.test(val) : true
                        },
                      },
                    })}
                    borderColor="gray.850"
                    color="white"
                    _placeholder={{
                      color: 'gray.850',
                    }}
                  />
                </Field>
                <Field
                  id="experienced_miner"
                  label={t('contact-sales.form.experience-miner')}
                  optional={true}
                  isInvalid={!!errors?.experienced_miner}
                  errorMessage={errors?.experienced_miner?.message || ''}
                >
                  <VStack
                    alignItems="flex-start"
                    css={{
                      '& .chakra-checkbox__control': {
                        borderColor: 'var(--chakra-colors-gray-840)',
                      },
                    }}
                  >
                    <Checkbox {...register('experienced_miner')} />
                    <Text fontSize={'12px'} color="gray.850">
                      {t('contact-sales.form.help-all-experience-levels')}
                    </Text>
                  </VStack>
                </Field>
                <Field
                  id="stages_in"
                  label={t('contact-sales.form.stages-in')}
                  isInvalid={!!errors?.stages_in}
                  errorMessage={errors?.stages_in?.message || ''}
                >
                  <Select
                    {...register('stages_in', {
                      required: t('forms.errors.is-required'),
                    })}
                    borderColor="gray.850"
                    color="white"
                    _placeholder={{
                      color: 'gray.850',
                    }}
                    css={{
                      '& option': {
                        background: 'var(--chakra-colors-gray-700)',
                      },
                    }}
                  >
                    <option value="">-</option>
                    <option value="existing_mining_operation">
                      {t('contact-sales.form.existing_mining_operation')}
                    </option>
                    <option value="rack_ready_not_hashing_yet">
                      {t('contact-sales.form.rack_ready_not_hashing_yet')}
                    </option>
                    <option value="greenfield_-_in_progress">
                      {t('contact-sales.form.greenfield_-_in_progress')}
                    </option>
                    <option value="greenfield_-_planned">
                      {t('contact-sales.form.greenfield_-_planned')}
                    </option>
                    <option value="other__please_describe_">
                      {t('contact-sales.form.other__please_describe_')}
                    </option>
                  </Select>
                </Field>
                <Field
                  id="facility_name"
                  label={t('contact-sales.form.facility-name')}
                  optional={true}
                  isInvalid={!!errors?.facility_name}
                  errorMessage={errors?.facility_name?.message || ''}
                >
                  <Input
                    {...register('facility_name')}
                    borderColor="gray.850"
                    color="white"
                    _placeholder={{
                      color: 'gray.850',
                    }}
                  />
                </Field>
                <Field
                  id="facility_location"
                  label={t('contact-sales.form.facility-location')}
                  optional={true}
                  isInvalid={!!errors?.facility_location}
                  errorMessage={errors?.facility_location?.message || ''}
                >
                  <VStack alignItems="flex-start" width="100%">
                    <Input
                      {...register('facility_location')}
                      borderColor="gray.850"
                      color="white"
                      _placeholder={{
                        color: 'gray.850',
                      }}
                    />
                    <Text fontSize={'12px'} color="gray.850">
                      {t('contact-sales.form.state-preferred')}
                    </Text>
                  </VStack>
                </Field>
                <Field
                  id="initial_capacity"
                  label={t('contact-sales.form.initial-capacity')}
                  optional={true}
                  isInvalid={!!errors?.initial_capacity}
                  errorMessage={errors?.initial_capacity?.message || ''}
                >
                  <Input
                    {...register('initial_capacity')}
                    borderColor="gray.850"
                    color="white"
                    _placeholder={{
                      color: 'gray.850',
                    }}
                  />
                </Field>
                <Field
                  id="site_extendable"
                  label={t('contact-sales.form.site-expandable')}
                  optional={true}
                  isInvalid={!!errors?.site_extendable}
                  errorMessage={errors?.site_extendable?.message || ''}
                >
                  <Checkbox
                    {...register('site_extendable')}
                    css={{
                      '& .chakra-checkbox__control': {
                        borderColor: 'var(--chakra-colors-gray-840)',
                      },
                    }}
                  />
                </Field>
                <Field
                  id="electricity_agreement"
                  label={t('contact-sales.form.electricity-locked-in')}
                  optional={true}
                  isInvalid={!!errors?.electricity_agreement}
                  errorMessage={errors?.electricity_agreement?.message || ''}
                >
                  <VStack alignItems="flex-start" width="100%">
                    <Input
                      {...register('electricity_agreement')}
                      borderColor="gray.850"
                      color="white"
                      _placeholder={{
                        color: 'gray.850',
                      }}
                    />
                    <Text fontSize={'12px'} color="gray.850">
                      {t('contact-sales.form.decribe-rates')}
                    </Text>
                  </VStack>
                </Field>
                <Center width="100%">
                  <Turnstile
                    siteKey={TURNSTILE_KEY}
                    onSuccess={() => setCaptchaSuccess(true)}
                  />
                </Center>
                <Center width="100%">
                  <Button
                    onClick={handleSubmit(onSubmit)}
                    background="brand.600"
                    color="white"
                    borderRadius="none"
                    width={{ base: '100%', md: '438px' }}
                    height={'50px'}
                    _hover={{
                      background: 'brand.600',
                      opacity: 0.8,
                    }}
                    isDisabled={!captchaSuccess}
                  >
                    {t(`buttons.submit`)}
                  </Button>
                </Center>
                <Center>
                  <Text
                    fontSize={{ base: '14px', md: '16px' }}
                    lineHeight={{ base: '24px' }}
                    width={'80%'}
                  >
                    {t(`accelerated.help-description`)}
                  </Text>
                </Center>
              </VStack>
            </Box>
          </Flex>
        </ContainerBase>
        <Center
          id="specialists"
          background="black"
          py={10}
          px={{ base: '16px', md: '60px', xl: 'unset' }}
        >
          <VStack
            spacing={{ base: '30px', md: '40px' }}
            width={{ base: '100%', lg: '750px' }}
          >
            <VStack spacing="16px">
              <Text fontSize={{ base: '24px', md: '40px' }}>
                {t(`accelerated.have-additional-questions.title`)}
              </Text>
              <Text fontSize={{ base: '11px', md: '16px' }} textAlign="center">
                {t.rich(`accelerated.have-additional-questions.description`, {
                  email: (children: ReactNode) => (
                    <Link href="mailto:accelerated@compassmining.io">
                      {children}
                    </Link>
                  ),
                })}
              </Text>
            </VStack>
            <Flex justifyContent="space-between" width="100%">
              <VStack spacing={{ base: '16px', md: '28px' }} textAlign="center">
                <Box
                  width={{ base: '120px', md: '300px' }}
                  height={{ base: '120px', md: '300px' }}
                >
                  <Image
                    src="/img/acc_by_compass_Karoon_Mackenchery.jpeg"
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                </Box>
                <VStack spacing="8px">
                  <Flex gap="2">
                    <Text
                      fontSize={{ base: '16px', md: '24px' }}
                      fontWeight="600"
                      lineHeight="normal"
                    >
                      Karoon Mackenchery
                    </Text>
                    <Link
                      isExternal
                      href="https://www.linkedin.com/in/karoon-mackenchery/"
                    >
                      <Center
                        boxSize={{ base: '20px', md: '24px' }}
                        bgColor="white"
                        borderRadius="2px"
                      >
                        <Icon as={LinkedinIcon} color="black" />
                      </Center>
                    </Link>
                  </Flex>

                  <Text
                    fontSize={{ base: '11px', md: '16px' }}
                    lineHeight="normal"
                  >
                    Director of Hosting Services
                  </Text>
                </VStack>
              </VStack>
              <VStack spacing={{ base: '16px', md: '28px' }} textAlign="center">
                <Box
                  width={{ base: '120px', md: '300px' }}
                  height={{ base: '120px', md: '300px' }}
                >
                  <Image
                    src="/img/acc_by_compass_Heather_Pierce.png"
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                </Box>
                <VStack spacing="8px">
                  <Flex gap="2">
                    <Text
                      fontSize={{ base: '16px', md: '24px' }}
                      fontWeight="600"
                      lineHeight="normal"
                    >
                      Heather Pierce
                    </Text>
                    <Link
                      isExternal
                      href="https://www.linkedin.com/in/heathervpierce330/"
                    >
                      <Center
                        boxSize={{ base: '20px', md: '24px' }}
                        bgColor="white"
                        borderRadius="2px"
                      >
                        <Icon as={LinkedinIcon} color="black" />
                      </Center>
                    </Link>
                  </Flex>
                  <Text
                    fontSize={{ base: '11px', md: '16px' }}
                    lineHeight="normal"
                  >
                    Sales, Managed Services
                  </Text>
                </VStack>
              </VStack>
            </Flex>
          </VStack>
        </Center>
      </Box>
    </>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default ApplyNowPage
